import i18n from '../i18n';
/**
 * Excel export for list screens.
 *
 * Two shapes, both rendered in the browser (no server round-trip):
 *  - exportView()     — exactly what the user is looking at: their active
 *                       filters, in the order shown, with the visible columns.
 *  - exportFullDump() — every record, every field, nested objects flattened
 *                       so nothing is hidden inside a JSON blob.
 */
import * as XLSX from 'xlsx';
import { toast } from 'sonner';
import { toDate, weekNo } from './dateFormat';

const ISO_DATE = /^\d{4}-\d{2}-\d{2}(T|$)/;

// Documents always use day-first dotted dates plus the ISO week: 21.10.2026 W43
const docDate = (v) => {
  const d = toDate(v);
  if (!d) return String(v);
  const p = (n) => String(n).padStart(2, '0');
  return `${p(d.getDate())}.${p(d.getMonth() + 1)}.${d.getFullYear()} W${weekNo(d)}`;
};

const cellValue = (v) => {
  if (v === null || v === undefined) return '';
  if (v instanceof Date) return docDate(v);
  if (typeof v === 'boolean') return v ? i18n.t('docx.yes') : i18n.t('docx.no');
  if (typeof v === 'number') return v;
  if (typeof v === 'string') return ISO_DATE.test(v) ? docDate(v.length > 10 ? v : v.slice(0, 10)) : v;
  return JSON.stringify(v);
};

const columnWidths = (header, body) =>
  header.map((h, i) => {
    const len = Math.max(
      String(h).length,
      8,
      ...body.map((row) => String(row[i] ?? '').length),
    );
    return { wch: Math.min(52, len + 2) };
  });

const stamp = () => new Date().toISOString().slice(0, 10);

// Callers whose filename already carries a date (a timesheet week, say)
// keep it as-is instead of collecting a second one.
const dated = (filename) => (/\d{4}-\d{2}-\d{2}$/.test(filename) ? filename : `${filename}-${stamp()}`);

const write = (filename, sheetName, header, body) => {
  const ws = XLSX.utils.aoa_to_sheet([header, ...body]);
  ws['!cols'] = columnWidths(header, body);
  ws['!autofilter'] = { ref: XLSX.utils.encode_range({ s: { r: 0, c: 0 }, e: { r: body.length, c: header.length - 1 } }) };
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, sheetName.slice(0, 31));
  XLSX.writeFile(wb, `${dated(filename)}.xlsx`);
};

/**
 * columns: [{ header, key }] or [{ header, value: (row) => any }]
 */
export function exportView({ filename, sheetName = 'Data', columns, rows }) {
  if (!rows || rows.length === 0) { toast.error('Nothing to export — the list is empty'); return; }
  const header = columns.map((c) => c.header);
  const body = rows.map((r) => columns.map((c) => cellValue(c.value ? c.value(r) : r[c.key])));
  write(filename, sheetName, header, body);
  toast.success(`${rows.length} row${rows.length === 1 ? '' : 's'} exported`);
}

const SKIP_KEYS = new Set(['logo_file', 'photo_ids', 'public_token']);

const flatten = (obj, prefix = '', out = {}) => {
  Object.entries(obj || {}).forEach(([k, v]) => {
    if (SKIP_KEYS.has(k)) return;
    const path = prefix ? `${prefix}.${k}` : k;
    if (v && typeof v === 'object' && !Array.isArray(v) && !(v instanceof Date)) flatten(v, path, out);
    else out[path] = v;
  });
  return out;
};

const prettify = (path) =>
  path
    .split('.')
    .map((seg) => seg.replace(/_/g, ' ').replace(/^./, (c) => c.toUpperCase()))
    .join(' · ');

/**
 * Every record with every field it carries — including nested objects
 * (site contact, end client…) flattened into their own columns.
 */
export function exportFullDump({ filename, sheetName = 'All fields', rows }) {
  if (!rows || rows.length === 0) { toast.error('Nothing to export — there are no records'); return; }
  const flat = rows.map((r) => flatten(r));
  const keys = [];
  const seen = new Set();
  flat.forEach((r) => Object.keys(r).forEach((k) => { if (!seen.has(k)) { seen.add(k); keys.push(k); } }));
  // id first, then the rest in first-seen order
  keys.sort((a, b) => (a === 'id' ? -1 : b === 'id' ? 1 : 0));
  const header = keys.map(prettify);
  const body = flat.map((r) => keys.map((k) => cellValue(r[k])));
  write(`${filename}-all-fields`, sheetName, header, body);
  toast.success(`${rows.length} record${rows.length === 1 ? '' : 's'} exported with ${keys.length} fields`);
}
